import React, { useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableHead,
  ListTableHeader,
  ListTableRow,
  SettingRow,
} from "@coval/ui"

import { KeyValueRowsEditor } from "@/components/forms/KeyValueRowsEditor"
import type { JsonObject } from "@/types/json"

import {
  createInitializationParameterDraftRow,
  formatInitializationParameterValue,
  initializationParameterRowsToRecord,
  initializationParametersToRows,
  type InitializationParameterDraftRow,
} from "../utils/initializationParameters"

/**
 * Key-value pairs handed to a chatbot when the conversation starts. The row
 * lists what is set; editing happens in a dialog with one line per pair, so
 * the settings list itself never turns into a form.
 */
export function PersonaInitializationParametersField({
  value,
  onChange,
  disabled,
}: {
  value: JsonObject
  onChange: (value: JsonObject) => void
  disabled: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [rows, setRows] = useState<InitializationParameterDraftRow[]>([])
  const entries = Object.entries(value)

  const open = () => {
    setRows(initializationParametersToRows(value))
    setIsOpen(true)
  }

  const updateRow = (
    id: number,
    patch: Partial<InitializationParameterDraftRow>,
  ) =>
    setRows((previous) =>
      previous.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    )

  const removeRow = (id: number) =>
    setRows((previous) => {
      const remaining = previous.filter((row) => row.id !== id)
      return remaining.length > 0
        ? remaining
        : [createInitializationParameterDraftRow()]
    })

  return (
    <>
      <SettingRow
        tooltipIcon="info"
        label="Initialization Parameters"
        tooltip="Key-value pairs passed to the chatbot when the conversation starts."
        stacked
        action={
          <Button
            type="button"
            variant="secondary"
            onClick={open}
            disabled={disabled}
            className="h-7 px-2 text-xs"
          >
            {entries.length > 0 ? "Edit" : "Add"}
          </Button>
        }
      >
        {entries.length > 0 ? (
          <div className="overflow-hidden rounded-md border border-coval-border">
            <ListTable className="table-fixed text-xs">
              <ListTableHeader>
                <ListTableRow>
                  <ListTableHead className="w-1/3 border-r border-coval-border">
                    Key
                  </ListTableHead>
                  <ListTableHead>Value</ListTableHead>
                </ListTableRow>
              </ListTableHeader>
              <ListTableBody className="font-mono">
                {entries.map(([key, entryValue]) => (
                  <ListTableRow key={key}>
                    <ListTableCell className="truncate border-r border-coval-border">
                      {key}
                    </ListTableCell>
                    <ListTableCell className="break-all">
                      {formatInitializationParameterValue(entryValue)}
                    </ListTableCell>
                  </ListTableRow>
                ))}
              </ListTableBody>
            </ListTable>
          </div>
        ) : (
          <p className="text-xs text-coval-text-muted">None</p>
        )}
      </SettingRow>

      <Dialog open={isOpen} onOpenChange={(next) => !next && setIsOpen(false)}>
        <DialogContent className="w-full max-w-xl">
          <DialogHeader>
            <DialogTitle>Initialization Parameters</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2">
            <KeyValueRowsEditor
              rows={rows}
              onChange={(id, patch) =>
                updateRow(id, {
                  ...patch,
                  ...(patch.value === undefined ? {} : { valueChanged: true }),
                })
              }
              onRemove={removeRow}
              onAdd={() =>
                setRows((previous) => [
                  ...previous,
                  createInitializationParameterDraftRow(),
                ])
              }
              keyLabel="Parameter key"
              valueLabel="Parameter value"
              addLabel="Add parameter"
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                onChange(initializationParameterRowsToRecord(rows))
                setIsOpen(false)
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
